import { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowUpTrayIcon,
  TrashIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  BuildingLibraryIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { craApi } from '../lib/api';
import { useTeamId } from '../hooks/useTeamId';
import FeatureGate from '../components/FeatureGate';
import { PageHeader, StatCard, LoadingSpinner } from '../components/ui';

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => currentYear - i);

const INCOME_BADGE: Record<string, string> = {
  Low: 'bg-red-100 text-red-700',
  Moderate: 'bg-amber-100 text-amber-700',
  Middle: 'bg-blue-100 text-blue-700',
  Upper: 'bg-green-100 text-green-700',
};

export default function CraSbLoansPage() {
  const { teamId } = useTeamId();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedYear, setSelectedYear] = useState<number | undefined>(undefined);
  const [importResult, setImportResult] = useState<any>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['craSbLoans', teamId, selectedYear],
    queryFn: () => craApi.listSbLoans(teamId!, selectedYear),
    enabled: !!teamId,
  });
  const loans = data?.data?.loans || [];
  const totalLoans = data?.data?.total || 0;

  const importMutation = useMutation({
    mutationFn: (file: File) => craApi.importSbLoans(teamId!, file),
    onSuccess: (res) => {
      setImportResult(res.data);
      toast.success(`Imported ${res.data.totalImported} SB loans`);
      queryClient.invalidateQueries({ queryKey: ['craSbLoans'] });
    },
    onError: () => toast.error('Failed to import SB loans'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => craApi.deleteSbLoans(teamId!, selectedYear),
    onSuccess: (res) => {
      toast.success(`Deleted ${res.data.deleted} loans`);
      setImportResult(null);
      queryClient.invalidateQueries({ queryKey: ['craSbLoans'] });
    },
    onError: () => toast.error('Failed to delete loans'),
  });

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) importMutation.mutate(file);
    e.target.value = '';
  }

  // Compute stats from loaded loans
  const insideAA = loans.filter((l: any) => l.isInsideAA).length;
  const outsideAA = loans.filter((l: any) => !l.isInsideAA).length;
  const byIncome: Record<string, number> = {};
  const byGar: Record<string, number> = {};
  const years = new Set<number>();

  for (const loan of loans) {
    if (loan.tractIncomeLevel) byIncome[loan.tractIncomeLevel] = (byIncome[loan.tractIncomeLevel] || 0) + 1;
    const garLabel = loan.garCode === '1' ? '≤$1M GAR' : '>$1M GAR';
    byGar[garLabel] = (byGar[garLabel] || 0) + 1;
    years.add(loan.dataYear);
  }

  return (
    <FeatureGate feature="module_cra">
      <div className="space-y-6">
        <PageHeader
          title="Small Business Loans"
          description="Import, review, and manage SB/SF loan data for CRA Dashboard (CRAD) analysis."
          actions={
            <div className="flex items-center gap-2">
              <select
                value={selectedYear || ''}
                onChange={(e) => setSelectedYear(e.target.value ? parseInt(e.target.value, 10) : undefined)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
              >
                <option value="">All Years</option>
                {YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <input ref={fileInputRef} type="file" accept=".xlsx,.csv" className="hidden" onChange={handleFileSelect} />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={importMutation.isPending}
                className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-teal-500 disabled:opacity-50"
              >
                <ArrowUpTrayIcon className="h-4 w-4" />
                {importMutation.isPending ? 'Importing...' : 'Import SB Loans'}
              </button>
              {totalLoans > 0 && (
                <button
                  onClick={() => { if (confirm(`Delete ${selectedYear ? selectedYear + ' ' : 'all '}SB loans?`)) deleteMutation.mutate(); }}
                  disabled={deleteMutation.isPending}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-100 disabled:opacity-50"
                >
                  <TrashIcon className="h-4 w-4" />
                  {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                </button>
              )}
            </div>
          }
        />

        {/* Import Result Summary */}
        {importResult && (
          <div className="rounded-lg border border-green-200 bg-green-50 p-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircleIcon className="h-5 w-5 text-green-600" />
              <h3 className="text-sm font-semibold text-green-800">Import Complete</h3>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
              <div><span className="text-green-600">Total:</span> <span className="font-medium">{importResult.totalImported}</span></div>
              <div><span className="text-green-600">Inside AA:</span> <span className="font-medium">{importResult.insideAA}</span></div>
              <div><span className="text-green-600">Outside AA:</span> <span className="font-medium">{importResult.outsideAA}</span></div>
              <div><span className="text-green-600">Sheet:</span> <span className="font-medium">{importResult.sheet}</span></div>
            </div>
            {importResult.byIncomeLevel && Object.keys(importResult.byIncomeLevel).length > 0 && (
              <div className="mt-2 flex gap-3 text-xs">
                {Object.entries(importResult.byIncomeLevel).map(([level, count]) => (
                  <span key={level} className={clsx('rounded-full px-2 py-0.5 font-medium', INCOME_BADGE[level] || 'bg-gray-100 text-gray-700')}>
                    {level}: {count as number}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Stats */}
        {totalLoans > 0 && (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <StatCard icon={BuildingLibraryIcon} label="Total Loans" value={totalLoans} />
            <StatCard icon={MapPinIcon} label="Inside AA" value={insideAA} />
            <StatCard icon={ExclamationTriangleIcon} label="Outside AA" value={outsideAA} />
            <StatCard icon={CheckCircleIcon} label="Years" value={Array.from(years).sort().join(', ')} />
          </div>
        )}

        {/* Loan Table */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : totalLoans === 0 ? (
          <div className="rounded-lg border bg-white p-12 text-center">
            <ArrowUpTrayIcon className="mx-auto h-10 w-10 text-gray-300" />
            <p className="mt-3 text-sm font-medium text-gray-900">No SB loans imported</p>
            <p className="mt-1 text-sm text-gray-500">
              Upload a Small Business loan file (Excel or CSV) in CRA file spec format to get started.
            </p>
          </div>
        ) : (
          <div className="rounded-lg border bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="px-3 py-2 text-left font-semibold text-gray-600">Loan #</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-600">Year</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-600">Type</th>
                    <th className="px-3 py-2 text-right font-semibold text-gray-600">Amount</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-600">MSA</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-600">State</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-600">County</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-600 text-sm">Census Tract</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-600">Tract Income</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-600">Income Level</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-600">Inside AA</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-600">Assessment Area</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-600">GAR</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-600">MMCT</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {loans.slice(0, 200).map((loan: any) => (
                    <tr key={loan.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-mono text-gray-900">{loan.loanNumber}</td>
                      <td className="px-3 py-2 text-gray-600">{loan.dataYear}</td>
                      <td className="px-3 py-2 text-gray-600">{loan.loanType}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-900">
                        ${typeof loan.loanAmount === 'number' ? (loan.loanAmount * 1000).toLocaleString() : loan.loanAmount}
                      </td>
                      <td className="px-3 py-2 text-gray-600 text-xs">{loan.msaName || loan.msaCode || '—'}</td>
                      <td className="px-3 py-2 text-gray-600 text-xs">{loan.stateName || loan.stateCode || '—'}</td>
                      <td className="px-3 py-2 text-gray-600 text-xs">{loan.countyName || loan.countyCode || '—'}</td>
                      <td className="px-3 py-2 font-mono text-gray-600 text-sm">{loan.censusTract || '—'}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                        {loan.tractIncome ? `${loan.tractIncome}%` : '—'}
                      </td>
                      <td className="px-3 py-2">
                        {loan.tractIncomeLevel ? (
                          <span className={clsx('rounded-full px-2 py-0.5 text-[10px] font-medium', INCOME_BADGE[loan.tractIncomeLevel] || 'bg-gray-100 text-gray-600')}>
                            {loan.tractIncomeLevel}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="px-3 py-2">
                        {loan.isInsideAA ? (
                          <CheckCircleIcon className="h-4 w-4 text-green-500" />
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-600">
                        {loan.assessmentArea?.name || '—'}
                      </td>
                      <td className="px-3 py-2">
                        {loan.garCode === '1' ? (
                          <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-medium text-blue-700">≤$1M</span>
                        ) : loan.garCode === '2' ? (
                          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600">&gt;$1M</span>
                        ) : '—'}
                      </td>
                      <td className="px-3 py-2">
                        {(loan.isFrbMmct || loan.isFdicMmct) ? (
                          <span className="rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-medium text-purple-700">MMCT</span>
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {totalLoans > 200 && (
                <div className="border-t bg-gray-50 px-4 py-2 text-xs text-gray-500">
                  Showing first 200 of {totalLoans} loans
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </FeatureGate>
  );
}
